import { Component, OnInit, Input, ViewChild, HostListener, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { ModalDialogComponent } from '../../../../shared/components/modal-dialog/modal-dialog.component';
import { ScheduleItem } from '../room-schedule-item/room-schedule-item.component';
import { TranslateService } from '@ngx-translate/core';
import { AllocationTableModel } from './room-allocation-desktop.component';
import { ICreateReservationParams } from '../create-reservation-dialog/create-reservation-dialog-base.component';
import { ResourceType, ResourceTypeHelper } from 'src/app/shared/models/resource-type';
import { ResourceTableRow } from 'src/app/shared/models/resource-table-row';

@Component({
  selector: 'app-room-allocation-table',
  templateUrl: './room-allocation-table.component.html',
  styleUrls: ['./room-allocation-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoomAllocationTableComponent implements OnInit {
  /**
  * Defines the component presentation style
  */
  @Input() useReservationMode: boolean;

  /**
  * Defines which data to display
  */
  @Input() useColumnsDataAsCaption: boolean;

  /**
   * Browsers inner width
   *
   * @private
   * @type {number}
   * @memberof RoomAllocationTableComponent
   */
  private innerWidth: number;

  /**
   * Class to display on element
   *
   * @type {string}
   * @memberof RoomAllocationTableComponent
   */
  @Input() class: string;

  private _model: AllocationTableModel;

  @Input() set model(value: AllocationTableModel) {
    this._model = value;
    this.InitClickeables();
  }

  get model() {
    return this._model;
  }

  allocationTable: AllocationTableModel;

  /**
   * Shows loading cycle if set to true
   *
   * @type {boolean}
   * @memberof RoomAllocationTableComponent
   */
  @Input() isLoading: boolean;

  private _currentDate: Date;

  /**
   * Currently selected date of calendar
   *
   * @type {Date}
   * @memberof RoomAllocationTableComponent
   */
  @Input() set currentDate(value: Date) {
    this._currentDate = value;
    this.InitClickeables();
  }

  get currentDate(): Date {
    return this._currentDate;
  }

  /**
   * Message to display if no results were found
   *
   * @type {string}
   * @memberof RoomAllocationTableComponent
   */
  @Input() emptyResultMessage: string;

  /**
   * Type of items to display (room or device)
   *
   * @type {ResourceType}
   * @memberof RoomAllocationTableComponent
   */
  @Input() type: ResourceType;

  @Input() timespanConflictMessageKey: string;

  /**
   * Event raised on click on "create new"
   *
   * @memberof RoomScheduleItemComponent
   */
  @Output() createReservation = new EventEmitter<ICreateReservationParams>();

  /**
   * Event raised on click on "open room schedule"
   *
   * @memberof RoomAllocationTableComponent
   */
  @Output() openSchedule = new EventEmitter<IRoomScheduleInfo>();

  /**
   * Event raised on click on day navigation buttons (<,>)
   * If argument is TRUE, navigation schould go backwards
   *
   * @memberof RoomAllocationTableComponent
   */
  @Output() navigateDate = new EventEmitter<boolean>();

  @ViewChild('roomInfoDialog') private modalRoomInfo: ModalDialogComponent;

  @ViewChild('occupancyAlertModal') occupancyAlertModal: ModalDialogComponent;

  modalRoomInfoTitle: string;

  timeStream: number[];

  /**
   * Currently selected room
   *
   * @type {number}
   * @memberof RoomAllocationTableComponent
   */
  activeItemId: number;

  openResourceInfoText: string;

  openResourceCalendarText: string;

  constructor(private translate: TranslateService) { }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.innerWidth = window.innerWidth;
  }

  ngOnInit() {

    if (this.type === undefined) {
      throw new Error(`prop '${nameof<RoomAllocationTableComponent>('type')}' must be defined on RoomAllocationTableComponent`);
    }

    this.translate.get(ResourceTypeHelper.getString(this.type).toUpperCase() + '_INFORMATION')
      .subscribe(text => this.modalRoomInfoTitle = text);

    this.translate.get('OPEN_' + ResourceTypeHelper.getString(this.type).toUpperCase() + '_INFORMATION')
      .subscribe(text => this.openResourceInfoText = text);

    this.translate.get('OPEN_' + ResourceTypeHelper.getString(this.type).toUpperCase() + '_CALENDAR')
      .subscribe(text => this.openResourceCalendarText = text);

    this.timeStream = [6, 9, 12, 15, 18, 21];
    this.currentDate = new Date(this.currentDate);
    this.innerWidth = window.innerWidth;
  }

  get hasResults(): boolean {
    return this.allocationTable !== undefined && this.allocationTable.displayItems.length !== 0;
  }

  get mainClasses(): string {
    return (this.class + ' ' + 'p-1').trim();
  }

  showBgLight(index: number) {
    return index % 2 === 0;
  }

  /**
  * Handel openRoomInfo callback of room-allocation-item
  *
  * @param {number} roomId
  * @memberof RoomAllocationComponent
  */
  onOpenRoomInfo(roomId: number) {
    this.activeItemId = roomId;
    this.modalRoomInfo.openModal();
  }

  /**
  * Handel openRoomInfo callback of room-allocation-item
  *
  * @param {number} roomId
  * @memberof RoomAllocationComponent
  */
  onOpenSchedule(info: IRoomScheduleInfo) {
    this.openSchedule.emit(info);
  }

  /**
  * Handle click on "create reservation"
  *
  * @memberof RoomAllocationComponent
  */
  onCreateReservation(params: ICreateReservationParams) {
    // todo: set time according to form
    if (params.to === null) {
      params.to = new Date(params.from.getTime() + 1000 * 3600);
    }

    this.createReservation.emit(params);
  }

  /**
   * Handle click on day navigate buttons (<, >)
   *
   * @param {boolean} backwards
   * @memberof RoomAllocationTableComponent
   */
  onClickNavigate(backwards: boolean) {
    this.navigateDate.emit(backwards);
  }

  /**
   * true if windows size is smaller or equal to 768px (Bootstrap's 'md' limit)
   *
   * @readonly
   * @type {boolean}
   * @memberof RoomAllocationTableComponent
   */
  get isMobileView(): boolean {
    return this.innerWidth < 768;
  }

  private InitClickeables() {
    if (!this.currentDate || !this._model) { return; }

    this.allocationTable = this._model;

    const startDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), this.currentDate.getDate());
    const endDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), this.currentDate.getDate(), 23, 59, 59, 999);

    this.allocationTable.displayItems.forEach((item: IDisplayItem) => {
      const emptyItems = ScheduleItem.createEmptyScheduleItems(
        item.resourceId,
        item.scheduleItems,
        new Date(startDate),
        new Date(endDate),
        item.isBookable
          ? (!item.warning ? this.translate.get('CLICK_TO_RESERVE') : this.translate.get('CLICK_TO_REQUEST'))
          : this.translate.get('NOT_BOOKABLE'),
          // ! REVIEW: awaiting specific specification
        item.isBookingAllowed
      );
      item.scheduleItems = item.scheduleItems.concat(emptyItems);
    });
  }

  alertConflict(timespan: ITimeSpan) {
    this.occupancyAlertModal.openModal();
  }

}

/**
 * Event args object to open room calendar
 *
 * @export
 * @interface IRoomScheduleInfo
 */
export interface IRoomScheduleInfo {

  /**
   * RoomId
   *
   * @type {number}
   * @memberof RoomScheduleInfo
   */
  readonly roomId: number;

  /**
   * Name of the room
   *
   * @type {string}
   * @memberof RoomScheduleInfo
   */
  readonly designation: string;

  /**
   * Is room itself available and user has necessary permissions
   * to book or request booking reservation
   *
   * @type {boolean}
   * @memberof RoomScheduleInfo
   */
  readonly isBookable: boolean;

  /**
   * Used to determine if a booking will be definitive or reservable
   *
   * @type {boolean}
   * @memberof RoomScheduleInfo
   */
  readonly hasWarnings: boolean;
}

export interface ITimeSpan {
  fromHour: number;
  fromMin: number;
  toHour: number;
  toMin: number;
  inUse: boolean;
}

export interface ITimeBlock {
  hour: number;
  events: ITimeSpan[];
}

export interface IDisplayItem {
  resourceId: number;
  designation: string;
  warning: string;
  isBookable: boolean;
  scheduleItems: ScheduleItem[];
  additionalColumsData: ResourceTableRow[];
  isRessourceAvailable: boolean;
  isBookingAllowed: boolean;
  actionLabel: string;
}
